import { useState } from 'react'
import { DemoChevron } from './demo-chevron'
import { useLingui } from '@lingui/react/macro'
import { CheckIcon } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button, ButtonAnchor, ButtonLink } from '@/components/ui/button'

// Monthly price per employee band; the lede's "from" figure is the cheapest per-employee rate among them.
const TIERS = [
  { employees: 25, price: 75 },
  { employees: 50, price: 135 },
  { employees: 150, price: 360 },
  { employees: 300, price: 630 },
] as const
const FROM_PER_EMPLOYEE = Math.min(...TIERS.map((tier) => tier.price / tier.employees))

function Feature(props: { children: string }) {
  return (
    <li className="text-muted-foreground flex gap-2.5 text-sm leading-normal">
      <span className="bg-primary/15 text-primary mt-px grid size-4.5 shrink-0 place-items-center rounded-full">
        <CheckIcon className="size-3" strokeWidth={3} />
      </span>
      <span>{props.children}</span>
    </li>
  )
}

/** Companies pick an employee band and see its price; professionals are free. */
export function PlansSection(props: { onDemo: () => void }) {
  const { t, i18n } = useLingui()
  const [selected, setSelected] = useState(1)
  const euros = (value: number, fractionDigits = 0) =>
    i18n.number(value, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    })
  const bandLabel = (employees: number) => t`Up to ${employees} employees`
  const tier = TIERS[selected] ?? TIERS[1]
  const fromPrice = euros(FROM_PER_EMPLOYEE, 2)

  const companyFeatures = [
    t`Access a dashboard of your talent's Psychographic Profile`,
    t`Evaluate your own Psychographic Profile and your employees'`,
    t`Describe the Psychographic Profile of your positions and your offers`,
    t`Compare your employees' profiles with your positions`,
    t`Compare your applicants' profiles with your offers`,
    t`Rank employees and applicants by their level of fit`,
    t`Generate suggestions and questions for your interviews`,
    t`Identify your company's psychographic diversity`,
    t`Identify training paths based on the Psychographic Profile`,
    t`Discover the Psychographic Profile of your teams and their members`,
    t`Access the market's psychographic profiles`,
    t`Compare any Psychographic Profile with any other`,
  ]
  const professionalFeatures = [
    t`Evaluate your Psychographic Profile`,
    t`Access your Competences profile`,
    t`Learn your Values profile`,
    t`Discover your Personality profile`,
  ]

  return (
    <section id="plans" className="bg-muted/40 scroll-mt-20 border-y py-20">
      <div className="mx-auto max-w-6xl px-5 sm:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-semibold tracking-tight text-balance sm:text-4xl">{t`Choose your plan`}</h2>
          <p className="text-muted-foreground mt-4 text-lg leading-relaxed text-pretty">
            {t`Choose the plan that best fits your needs, from ${fromPrice} per employee per month`}
          </p>
        </div>

        <div className="mt-12 grid items-start gap-6 md:grid-cols-[1.45fr_1fr]">
          <div className="bg-card rounded-2xl border p-6 shadow-[0_30px_70px_-44px_rgb(24_54_48/0.42)] sm:p-8">
            <div className="flex items-center gap-2.5">
              <h3 className="text-xl font-semibold">{t`Companies`}</h3>
              <Badge variant="secondary">{t`14 days free`}</Badge>
            </div>

            <fieldset className="mt-5 grid gap-2">
              <legend className="sr-only">{t`Employee band`}</legend>
              {TIERS.map((band, index) => {
                const price = euros(band.price)
                return (
                  <label
                    key={band.employees}
                    className="hover:bg-muted/40 has-checked:border-primary has-checked:bg-primary/5 has-checked:ring-primary/15 has-focus-visible:ring-ring/50 flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm font-medium transition-colors has-checked:ring-3 has-focus-visible:ring-3"
                  >
                    <input
                      type="radio"
                      name="employee-band"
                      className="accent-primary size-4"
                      checked={index === selected}
                      onChange={() => setSelected(index)}
                    />
                    <span>{bandLabel(band.employees)}</span>
                    <b className="ml-auto font-semibold whitespace-nowrap tabular-nums">{t`${price}/month`}</b>
                  </label>
                )
              })}
            </fieldset>
            <p className="text-muted-foreground mt-3 text-sm">
              {t`More than 300 employees?`}{' '}
              <Button variant="link" type="button" className="h-auto p-0 align-baseline" onClick={props.onDemo}>
                {t`Contact us`}
              </Button>
            </p>

            <div className="mt-6 flex items-baseline gap-2">
              <span className="text-4xl font-semibold tracking-tight tabular-nums">{euros(tier.price)}</span>
              <span className="text-muted-foreground text-sm">
                {t`/month`} · {bandLabel(tier.employees).toLowerCase()}
              </span>
            </div>

            <ul className="mt-6 grid gap-3">
              {companyFeatures.map((feature) => (
                <Feature key={feature}>{feature}</Feature>
              ))}
            </ul>

            <div className="mt-7 flex flex-wrap gap-2.5">
              <ButtonLink size="lg" to="/register/company">{t`Get a plan`}</ButtonLink>
              <Button size="lg" variant="outline" type="button" onClick={props.onDemo}>
                {t`Request a demo`}
                <DemoChevron />
              </Button>
              <ButtonLink size="lg" variant="ghost" to="/register/company">{t`Try free for 14 days`}</ButtonLink>
            </div>
          </div>

          <div className="bg-card rounded-2xl border p-6 sm:p-8">
            <h3 className="text-xl font-semibold">{t`Professionals`}</h3>
            <span className="text-primary mt-2 inline-block text-sm font-semibold tracking-widest uppercase">
              {t`Free`}
            </span>
            <ul className="mt-6 grid gap-3">
              {professionalFeatures.map((feature) => (
                <Feature key={feature}>{feature}</Feature>
              ))}
            </ul>
            <div className="mt-7">
              <ButtonAnchor size="lg" variant="outline" href="#professionals">{t`Sign up for free`}</ButtonAnchor>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
